import ShoppingBasketRoundedIcon from '@mui/icons-material/ShoppingBasketRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { Stack } from '@mui/material';

import { useBasketSettings, useUpdateBasketSettings } from '../../hooks';
import { toSettingsUpdate, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type { CartMergeStrategy } from '../../model/settingsTypes';
import {
  FeedbackSnackbar,
  NumberField,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
} from '../components';
import { CART_MERGE_STRATEGY_OPTIONS } from '../options';

/** Basket settings: cart lifetime, limits and guest-merge behaviour. */
export function BasketSettingsPage() {
  const { canView, canManage } = useDomainPermissions('basket');
  const query = useBasketSettings();
  const update = useUpdateBasketSettings();
  const form = useSettingsForm(query.data);
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success('Basket settings saved.'),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title="Basket settings"
      subtitle="Cart lifetime, limits and guest-merge behaviour."
      icon={<ShoppingBasketRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title="Cart behaviour" icon={<ShoppingBasketRoundedIcon />}>
              <SwitchField
                label="Guest cart"
                description="Allow shopping without an account."
                checked={draft.guestCartEnabled}
                onChange={(value) => form.setField('guestCartEnabled', value)}
                disabled={disabled}
              />
              <SelectField
                label="Merge strategy on login"
                description="What happens to a guest cart when the customer signs in."
                value={draft.mergeStrategyOnLogin}
                options={CART_MERGE_STRATEGY_OPTIONS}
                onChange={(value) =>
                  form.setField('mergeStrategyOnLogin', value as CartMergeStrategy)
                }
                disabled={disabled}
              />
              <SwitchField
                label="Reserve stock on add"
                description="Hold stock as soon as an item is added to the cart."
                checked={draft.reserveStockOnAdd}
                onChange={(value) => form.setField('reserveStockOnAdd', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Save for later"
                checked={draft.saveForLaterEnabled}
                onChange={(value) => form.setField('saveForLaterEnabled', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard title="Limits" icon={<TuneRoundedIcon />}>
              <NumberField
                label="Cart lifetime"
                description="How long an inactive cart is kept."
                value={draft.cartTtlMinutes}
                onChange={(value) => form.setField('cartTtlMinutes', value)}
                disabled={disabled}
                min={0}
                suffix="minutes"
              />
              <NumberField
                label="Max line items"
                value={draft.maxLineItems}
                onChange={(value) => form.setField('maxLineItems', value)}
                disabled={disabled}
                min={1}
              />
              <NumberField
                label="Max quantity per item"
                value={draft.maxQuantityPerItem}
                onChange={(value) => form.setField('maxQuantityPerItem', value)}
                disabled={disabled}
                min={1}
              />
              <NumberField
                label="Minimum order amount"
                value={draft.minOrderAmount}
                onChange={(value) => form.setField('minOrderAmount', value)}
                disabled={disabled}
                min={0}
              />
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
